import { useMemo } from 'react';
import { Organization, User } from 'contexts';

/**
 * Adapt the shell's nullable-property auth shapes (`User` / `Organization`
 * from AuthContext) into the structural shapes `useEventComposerConfig`
 * expects (every field present, value may be nullish).
 *
 * Memoized so the composer config derived from these keeps a stable identity
 * across re-renders. Rebuilding the objects every render gave the config (and
 * therefore the patterns ConfigProvider's `userContext`) a new identity each
 * time, which re-fired data-loading effects downstream — e.g.
 * EventDetailContextProvider issued duplicate GET /v2.0/events/{id} requests
 * while the first one was still in flight.
 *
 * Shared by `EventOverlayHost` and `EventOverlayHostLegacy` so the two
 * flag-gated implementations cannot drift.
 */
export const useAdaptedUserAndOrg = (
  currentUser: User | null,
  currentOrg: Organization | null
) => {
  const adaptedUser = useMemo(
    () =>
      currentUser
        ? {
            id: currentUser.id,
            name: currentUser.name ?? null,
            timeZone: currentUser.timeZone ?? null,
            avatar: currentUser.avatar ?? null,
            primaryEmail: currentUser.primaryEmail
              ? { email: currentUser.primaryEmail.email }
              : null,
          }
        : null,
    [currentUser]
  );

  const adaptedOrg = useMemo(
    () =>
      currentOrg
        ? {
            id: currentOrg.id,
            name: currentOrg.name ?? null,
            slug: currentOrg.slug ?? null,
            avatar: currentOrg.avatar ?? null,
            logo: currentOrg.logo ?? null,
            features: currentOrg.features
              ? {
                  maps: currentOrg.features.maps,
                  workweek: currentOrg.features.workweek,
                  peopleDirectory: currentOrg.features.peopleDirectory,
                  // Shell's AuthContext doesn't carry visitorManagement on
                  // currentOrg.features; default to false until added.
                  visitorManagement: false,
                }
              : null,
          }
        : null,
    [currentOrg]
  );

  return { adaptedUser, adaptedOrg };
};
